import React, { useEffect, useState } from 'react';
import { Box, Button, Flex, Heading, useToast } from '@chakra-ui/react';
import { AddIcon } from '@chakra-ui/icons';
import DocumentManagementTable from './DocumentManagementTable';
import { useNavigate, useLocation } from 'react-router-dom';
import DocumentService from '../../services/DocumentService.ts';
import { IDocumentListResponse } from '../../interfaces/Document/IDocumentListResponse.ts';
import { IDocument } from '../../interfaces/Document/IDocument.ts';
import { useTranslation } from 'react-i18next';
import { usePageFiltersStorage } from '../../hooks/usePageFiltersStorage';

const DocumentManagementPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { isRestoring } = usePageFiltersStorage();
  const toast = useToast();

  // State for documents
  const [documents, setDocuments] = useState<IDocument[]>([]);

  // State for loading spinner
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // State for current page
  const [_currentPage, setCurrentPage] = useState(1);

  // Get total number of documents
  const [totalCount, setTotalCount] = useState(0);

  // Get URL search params
  const queryParams = new URLSearchParams(location.search);

  // Get search query from URL
  const initialSearchQuery = queryParams.get('query') || '';

  // State for search query
  const [searchQuery, setSearchQuery] = useState('');

  // State for search bar visibility
  const [isSearchOpen, setIsSearchOpen] = useState(!!initialSearchQuery);

  // State for data fetching status
  const [isTableListFetched, setIsTableListFetched] = useState(false);

  const fetchDocuments = async (page = 1) => {
    setIsLoading(true);
    try {
      const response: IDocumentListResponse =
        await DocumentService.getDocuments({
          ...Object.fromEntries(new URLSearchParams(location.search)),
          page,
          per_page: 10,
        });
      setDocuments((prevDocuments) => [
        ...(page === 1 ? [] : prevDocuments),
        ...response.items,
      ]);
      setCurrentPage(page);
      setTotalCount(response.total);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
      // Clear documents on error
      setDocuments([]);

      // Set total count to 0
      setTotalCount(0);

      // Set current page to 1
      setCurrentPage(1);

      // Show error toast
      toast({
        title: t('error'),
        description: t('failedToFetchDocuments'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
      setIsTableListFetched(true);
    }
  };

  useEffect(() => {
    if (isRestoring) return;
    fetchDocuments(1);
  }, [isRestoring, location.search]);

  const handleAddDocumentClick = () => {
    navigate('/document-management/add');
  };

  return (
    <Box>
      <Flex justifyContent="space-between" alignItems="center" mb="10px" h="75">
        <Heading size="lg" fontSize={25} fontWeight={500} pl={25}>
          {t('documentManagement')}
        </Heading>
        <Button
          leftIcon={<AddIcon />}
          colorScheme="teal"
          variant="solid"
          h="50px"
          borderRadius="20px"
          fontWeight={400}
          onClick={handleAddDocumentClick}
        >
          {t('addDocument')}
        </Button>
      </Flex>

      <Box p={6} bg="white" borderRadius="20px" overflow="auto">
        <DocumentManagementTable
          documents={documents}
          isLoading={isLoading}
          total={totalCount}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          isSearchOpen={isSearchOpen}
          setIsSearchOpen={setIsSearchOpen}
          isTableListFetched={isTableListFetched}
        />
      </Box>
    </Box>
  );
};

export default DocumentManagementPage;
